'use client';

import { useState } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Box, Stack, Typography } from '@mui/material';
import {
  ClubLogo,
  fontSize,
  CompetitionRankingColumnDefault,
} from '@scorenco/components';
import { SportType, Types } from '@scorenco/core';

import { Game } from './_internal';

type GetTournamentPhaseData = {
  tournaments_phase_by_pk?: {
    id: number;
    name?: string;
    rank?: number;
    engagements?: Array<{
      id: number;
      name?: string;
      ranking?: {
        rank?: number;
        id: number;
        pts?: number;
        played?: number;
        win?: number;
        lost?: number;
        even?: number;
        gf?: number;
        ga?: number;
        gd?: number;
        bonus?: number;
      } | null;
      team?: { id: number; name?: string; logo?: string } | null;
    }>;
    games?: Array<{
      game: {
        id: number;
        date?: string;
        name?: string;
        place?: string;
        game_type?: number;
        url?: string;
        status?: string;
        home_score?: number;
        home_score_data?: unknown;
        home_shootout?: number | null;
        away_score?: number;
        away_score_data?: unknown;
        away_shootout?: number | null;
        home_engagement?: {
          id: number;
          name?: string;
          team?: { id: number; name?: string; logo?: string } | null;
        } | null;
        away_engagement?: {
          id: number;
          name?: string;
          team?: { id: number; name?: string; logo?: string } | null;
        } | null;
      };
    }>;
    rule?: {
      id: number;
      name?: string;
      nb_groups?: number;
      nb_teams?: number;
      nb_teams_in_final_phase?: number;
      period_duration?: number;
      rank?: number;
      rule_type?: number;
      time_between_periods?: number;
      period_nb?: number;
      ranking_point?: { id: number; use_win_column_first?: boolean } | null;
    } | null;
  } | null;
  tournaments_tournament?: Array<{
    id: number;
    name?: string;
    sport_id?: number;
    challenges?: Array<{ id: number; name?: string }>;
  }>;
};

type PhaseData = NonNullable<GetTournamentPhaseData['tournaments_phase_by_pk']>;
type PhaseGame = NonNullable<PhaseData['games']>[number];
type PhaseEngagement = NonNullable<PhaseData['engagements']>[number];

const getGameTypes = (step: number): number[] | null => {
  if (step === 1) {
    return [1, 3, 6, 7];
  }

  if (step === 2) {
    return [2, 5];
  }

  if (step === 3) {
    return [4];
  }

  if (step === 4) {
    return [8];
  }

  return null;
};

interface CupModeProps {
  games: PhaseGame[];
  nbGroups: number;
  screen: Types.Clubhouse_Screen;
}

const CupMode = ({ games, nbGroups, screen }: CupModeProps) => {
  const stepsArray = new Array(games.length ? games.length / nbGroups : 1)
    .fill(undefined)
    .map((_x, i) => i)
    .reverse();

  const [steps] = useState(() =>
    stepsArray.map((step) => ({
      games:
        getGameTypes(step + 1)?.map((gameType) =>
          games.filter((game: PhaseGame) => game.game.game_type === gameType)
        ) ?? [],
    }))
  );

  return (
    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
      {steps.map((step, i) => {
        const totalGameLength = step.games
          .map((gamesByType) => gamesByType.length)
          .reduce((a, b) => a + b, 0);

        if (totalGameLength === 0) {
          return null;
        }

        return (
          <Box key={i} sx={{ flex: 1 }}>
            {step.games.map((gamesByType, groupIndex) => {
              return gamesByType.map((game, gameIndex) => {
                const gameDate =
                  game.game?.status !== 'to_come' && game.game?.date
                    ? new Date(game.game.date).toLocaleDateString([], {
                        month: 'short',
                        day: '2-digit',
                      })
                    : null;

                return (
                  <Box
                    key={`${groupIndex}-${gameIndex}-${game.game.id}`}
                    css={css`
                      @keyframes scrollEventCup {
                        0% {
                          transform: translateY(0);
                        }
                        15% {
                          transform: translateY(0);
                        }
                        85% {
                          transform: translateY(
                            -${(totalGameLength - 4) * 8.5}rem
                          );
                        }
                        100% {
                          transform: translateY(
                            -${(totalGameLength - 4) * 8.5}rem
                          );
                        }
                      }
                      animation: ${totalGameLength > 4
                        ? `scrollEventCup ${screen?.duration}s  linear infinite`
                        : ''};
                    `}
                  >
                    <h3
                      css={css`
                        font-size: 1.5rem;
                        text-align: center;
                        margin-bottom: 0.5rem;
                      `}
                    >
                      {game.game.name}
                    </h3>
                    <Box
                      css={(theme) => css`
                        padding: 0.5rem 1rem;
                        border: 1px solid #e2e8ed;
                        color: ${theme.vars.palette.text.primary};
                        background-color: ${theme.vars.palette.background
                          .paper};
                        ${fontSize.base(theme)}
                        transition: box-shadow 0.5s cubic-bezier(0.165, 0.84, 0.44, 1), background-color 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
                        min-height: 5rem;
                        box-sizing: content-box;
                      `}
                    >
                      <Game game={game} />
                      <Box
                        css={css`
                          text-align: center;
                          font-size: 1.2rem;
                        `}
                      >
                        {[game.game?.place, gameDate]
                          .filter(Boolean)
                          .join(' - ')}
                      </Box>
                    </Box>
                  </Box>
                );
              });
            })}
          </Box>
        );
      })}
    </Stack>
  );
};

type EngagementRowProps = {
  engagement: NonNullable<
    NonNullable<
      GetTournamentPhaseData['tournaments_phase_by_pk']
    >['engagements']
  >[number];
  showBonus: boolean;
  useWinColumnFirst?: boolean;
};

const EngagementRow = ({
  engagement,
  showBonus,
  useWinColumnFirst,
}: EngagementRowProps) => {
  return (
    <Box
      css={css`
        display: flex;
        border: 1px solid #e2e8ed;
        background: white;
        padding: 0.5rem 1rem;
        margin-top: -1px;
        font-size: 1.5rem;
      `}
    >
      <Box
        css={css`
          flex: 0 0 1.5rem;
          text-align: center;
        `}
      >
        {engagement.ranking?.rank ?? '-'}
      </Box>
      <Box
        css={css`
          margin-left: 0.25rem;
          flex: 1;
        `}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
          }}
        >
          <ClubLogo
            style={{
              height: '1.5rem',
              width: '1.25rem',
              marginRight: '1rem',
            }}
            width={32}
            height={32}
            logo={engagement.team?.logo}
            name={engagement.team?.name || engagement?.name}
            shieldColor="home"
          />
          {engagement.team?.name || engagement.name}
        </Box>
      </Box>
      <CompetitionRankingColumnDefault
        fontSize="1.5rem"
        value={
          useWinColumnFirst
            ? (engagement.ranking?.win ?? '-')
            : (engagement.ranking?.pts ?? '-')
        }
      />
      <CompetitionRankingColumnDefault
        fontSize="1.5rem"
        value={engagement.ranking?.played ?? '-'}
      />
      <CompetitionRankingColumnDefault
        fontSize="1.5rem"
        value={
          useWinColumnFirst
            ? (engagement.ranking?.pts ?? '-')
            : (engagement.ranking?.win ?? '-')
        }
      />
      <CompetitionRankingColumnDefault
        fontSize="1.5rem"
        value={engagement.ranking?.even ?? '-'}
      />
      <CompetitionRankingColumnDefault
        fontSize="1.5rem"
        value={engagement.ranking?.lost ?? '-'}
      />
      <CompetitionRankingColumnDefault
        fontSize="1.5rem"
        value={engagement.ranking?.gf ?? '-'}
      />
      <CompetitionRankingColumnDefault
        fontSize="1.5rem"
        value={engagement.ranking?.ga ?? '-'}
      />
      <CompetitionRankingColumnDefault
        fontSize="1.5rem"
        value={engagement.ranking?.gd ?? '-'}
      />
      {showBonus && (
        <CompetitionRankingColumnDefault
          fontSize="1.5rem"
          value={engagement.ranking?.bonus ?? '-'}
        />
      )}
    </Box>
  );
};

export const TournamentPhase = ({
  screen,
}: {
  screen: Types.Clubhouse_Screen;
}) => {
  const getPhase = useQuery<GetTournamentPhaseData>(
    gql`
      query TournamentPhase_GetPhase($phaseId: Int!) {
        tournaments_phase_by_pk(id: $phaseId) {
          id
          name
          rank
          engagements(order_by: { initial_rank: asc }) {
            id
            name
            ranking {
              rank
              id
              pts
              played
              win
              lost
              even
              gf
              ga
              gd
            }
            team {
              id
              name
              logo
            }
          }
          games(order_by: [{ game: { date: asc } }, { game: { place: asc } }]) {
            game {
              id
              date
              name
              place
              game_type
              url
              status
              home_score
              home_score_data
              home_shootout
              away_score
              away_score_data
              away_shootout
              home_engagement {
                id
                name
                team {
                  id
                  name
                  logo
                }
              }
              away_engagement {
                id
                name
                team {
                  id
                  name
                  logo
                }
              }
            }
          }
          rule {
            id
            name
            nb_groups
            nb_teams
            nb_teams_in_final_phase
            period_duration
            rank
            rule_type
            time_between_periods
            period_nb
            ranking_point {
              id
              use_win_column_first
            }
          }
        }
        tournaments_tournament(
          where: {
            challenges: { rules: { phases: { id: { _eq: $phaseId } } } }
          }
        ) {
          id
          name
          sport_id
          challenges(where: { rules: { phases: { id: { _eq: $phaseId } } } }) {
            id
            name
          }
        }
      }
    `,
    {
      variables: {
        phaseId: screen.phase_id,
      },
      skip: !screen.phase_id,
      context: {
        role: 'club',
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const phase = getPhase?.data?.tournaments_phase_by_pk;
  const rule = phase?.rule;
  const phaseGames = phase?.games ?? [];
  const engagements = phase?.engagements ?? [];
  const rulePeriodDuration = rule?.period_duration ?? 0;
  const isVolley =
    getPhase.data?.tournaments_tournament?.[0]?.sport_id === SportType.Volley;

  const eventToDisplay =
    rule?.rule_type === 3 || (rule?.rule_type === 6 && phase?.rank === 1)
      ? 4
      : 6;

  const ranking =
    engagements.length > 2 &&
    engagements[0]?.ranking?.rank &&
    engagements
      .slice()
      .sort(
        (e1: PhaseEngagement, e2: PhaseEngagement) =>
          (e1.ranking?.rank ?? 0) - (e2.ranking?.rank ?? 0)
      );

  const rankingRows = Array.isArray(ranking) ? ranking : [];
  const gameScrollOffset = (phaseGames.length - eventToDisplay) * 6.5;

  const style = {
    root: css`
      .animation {
        animation: scrollEvent 15s linear infinite;
      }

      @keyframes scrollEvent {
        0% {
          transform: translateY(0);
        }
        15% {
          transform: translateY(0);
        }
        85% {
          transform: translateY(-${gameScrollOffset}rem);
        }
        100% {
          transform: translateY(-${gameScrollOffset}rem);
        }
      }
    `,
  };

  const hasBonus = rankingRows.some(
    (engagement) => (engagement?.ranking?.bonus ?? 0) > 0
  );

  return (
    <Box css={style.root}>
      <Typography
        variant="h1"
        sx={{
          m: 0,
          textAlign: 'center',
          marginTop: '3rem',
        }}
      >
        {getPhase?.data?.tournaments_tournament?.[0]?.name}
      </Typography>
      <Typography
        variant="h2"
        sx={{
          m: 0,
          textAlign: 'center',
          // marginTop: '3rem',
        }}
      >
        {getPhase?.data?.tournaments_tournament?.[0]?.challenges?.[0]?.name}
      </Typography>
      <Typography
        variant="h3"
        css={css`
          margin: 0.75rem 1rem 1.8rem;
          font-size: 1.5rem;
          text-align: center;
        `}
      >
        {phase?.name}

        {isVolley && rule ? (
          <>
            <span
              css={css`
                margin-left: 1rem;
                font-weight: 500;
              `}
            >
              (Matchs de {rule.period_nb} sets gagnants)
            </span>
          </>
        ) : (
          rule &&
          rulePeriodDuration > 0 && (
            <span
              css={css`
                margin-left: 1rem;
                font-weight: 500;
              `}
            >
              (
              {rule.period_nb === 1
                ? `Matchs de ${rulePeriodDuration} minutes`
                : `Mi-temps de ${rulePeriodDuration} minutes`}
              )
            </span>
          )
        )}
      </Typography>

      <Stack direction="row" spacing={4} sx={{ width: '100%', px: 4, mt: 4 }}>
        {rankingRows.length > 0 && (
          <Box sx={{ flex: 1, marginBottom: '1rem' }}>
            <Box
              css={css`
                font-size: 3rem;
              `}
            >
              <Box
                css={css`
                  display: flex;
                  padding: 0.25rem 1rem;
                `}
              >
                <Box
                  css={css`
                    flex: 1;
                    font-size: 1.2rem;
                  `}
                />
                <CompetitionRankingColumnDefault
                  fontSize="1.2rem"
                  value={
                    rule?.ranking_point?.use_win_column_first ? 'G.' : 'Pts'
                  }
                />
                <CompetitionRankingColumnDefault fontSize="1.2rem" value="Jo" />
                <CompetitionRankingColumnDefault
                  fontSize="1.2rem"
                  value={
                    rule?.ranking_point?.use_win_column_first ? 'Pts' : 'G.'
                  }
                />
                <CompetitionRankingColumnDefault fontSize="1.2rem" value="N." />
                <CompetitionRankingColumnDefault fontSize="1.2rem" value="P." />
                <CompetitionRankingColumnDefault fontSize="1.2rem" value="p." />
                <CompetitionRankingColumnDefault fontSize="1.2rem" value="c." />
                <CompetitionRankingColumnDefault fontSize="1.2rem" value="d." />
                {hasBonus && (
                  <CompetitionRankingColumnDefault
                    fontSize="1.2rem"
                    value="Bon."
                  />
                )}
              </Box>
              {rankingRows.map((engagement, idxEngagement) => (
                <EngagementRow
                  key={`${idxEngagement}`}
                  engagement={engagement}
                  showBonus={hasBonus}
                  useWinColumnFirst={
                    rule?.ranking_point?.use_win_column_first || false
                  }
                />
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ flex: 2 }}>
          <Box
            sx={{
              maxHeight: '50rem',
              height: '50rem',
              overflow: 'hidden',
            }}
          >
            {rule?.rule_type === 3 ? (
              <CupMode
                games={phaseGames}
                nbGroups={rule.nb_groups ?? 1}
                screen={screen}
              />
            ) : (
              <>
                {phaseGames.map((game, idxGame) => {
                  const gameDate =
                    game.game?.status !== 'to_come' && game.game?.date
                      ? new Date(game.game.date).toLocaleDateString([], {
                          month: 'short',
                          day: '2-digit',
                        })
                      : null;

                  return (
                    <Box
                      key={`${idxGame}`}
                      css={(theme) => css`
                        padding: 0.5rem 1rem;
                        border: 1px solid #e2e8ed;
                        color: ${theme.vars.palette.text.primary};
                        background-color: ${theme.vars.palette.background
                          .paper};
                        ${fontSize.base(theme)}
                        transition: box-shadow 0.5s cubic-bezier(0.165, 0.84, 0.44, 1), background-color 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
                        margin-bottom: -1px;
                        min-height: 5rem;
                        box-sizing: content-box;
                        animation: ${phaseGames.length > eventToDisplay
                          ? `scrollEvent ${screen?.duration}s linear infinite`
                          : ''};
                      `}
                    >
                      <Game key={`${idxGame}`} game={game} />
                      <Box
                        css={css`
                          text-align: center;
                          font-size: 1.2rem;
                        `}
                      >
                        {[game.game?.place, gameDate]
                          .filter(Boolean)
                          .join(' - ')}
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
